import { useMutation } from '@apollo/client/react';
import { VERIFY_PHONE_NUMBER_MUTATION } from '../../integrations/allkort/graphql/gql/PhoneMutations';
import { VerifyPhoneNumberResponse } from '../../integrations/allkort/graphql/interface/Phones';

/**
 * useVerifyPhone
 *
 * Custom hook for verifying a phone number using the SMS code sent after addPhoneNumbers.
 *
 * @example
 * ```tsx
 * const { verifyPhone, loading } = useVerifyPhone();
 *
 * const handleVerify = async () => {
 *   const result = await verifyPhone({ phoneNumberId: 939748, verificationCode: '4713' });
 *   if (result?.verified) console.log('Verified phone:', result.number);
 * };
 * ```
 */
export const useVerifyPhone = () => {
	const [verifyPhoneNumberMutation, { loading, error, data }] =
		useMutation<VerifyPhoneNumberResponse>(VERIFY_PHONE_NUMBER_MUTATION);

	/**
	 * Verify a phone number with the SMS code
	 * @param phoneNumberId    - The ID of the unverified phone number (from addPhoneNumbers response)
	 * @param verificationCode - The SMS code entered by the user
	 * @returns The verified PhoneNumber object, or throws on failure
	 */
	const verifyPhone = async ({
		phoneNumberId,
		verificationCode,
	}: {
		phoneNumberId: number
		verificationCode: string
	}) => {
		try {
			const result = await verifyPhoneNumberMutation({
				variables: { phoneNumberId, verificationCode },
			});

			if (result.error) {
				const apolloError = result.error as any;
				const message = apolloError.graphQLErrors?.[0]?.message ?? apolloError.message;
				console.error('[useVerifyPhone] GraphQL error:', message);
				throw new Error(message);
			}

			const phoneNumber = result.data?.verifyPhoneNumber?.phoneNumber ?? null;
			console.log('[useVerifyPhone] Verification successful:', phoneNumber);
			return phoneNumber;
		} catch (err) {
			// Re-throw so the caller can display the error message
			throw err;
		}
	};

	return { verifyPhone, loading, error, data };
};
